import React, { useState, useEffect, useRef } from 'react';
import Layout from '../../components/Layout';
import { productsAPI, ordersAPI, paymentsAPI, promoCodesAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  ArrowLeft, ArrowRight, Ban, CheckCircle, CreditCard, Package, MapPin, Loader2,
  Ruler, Upload, MessageCircle, Send, Camera, X
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import api from '../../services/api';
import axios from 'axios';
import { FilterSelector } from './checkout/FilterSelector';
import { ShippingStep } from './checkout/ShippingStep';
import { ReviewAndPay } from './checkout/ReviewAndPay';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const TAX_RATE = 0.0825;
const SUBSCRIPTION_DISCOUNT = 0;

const STEPS = [
  { id: 1, label: 'Select Filters', icon: Package },
  { id: 2, label: 'Shipping', icon: MapPin },
  { id: 3, label: 'Review & Pay', icon: CreditCard },
];

const NewOrder = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const preselectedSize = searchParams.get('size');
  const preselectedMerv = searchParams.get('merv');
  const reorderItems = location.state?.reorderItems;
  const editOrder = location.state?.editOrder;

  const [step, setStep] = useState(1);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orderItems, setOrderItems] = useState(
    editOrder?.items?.length > 0
      ? editOrder.items.map(item => ({ size: item.size, merv: String(item.merv), quantity: String(item.quantity), price: item.price, minQty: 1, source: '' }))
      : reorderItems && reorderItems.length > 0
        ? reorderItems.map(item => ({ ...item, minQty: 1, source: '' }))
        : [{ size: preselectedSize || '', merv: preselectedMerv || '8', quantity: '1', price: 0, minQty: 1, source: '' }]
  );
  const getFullAddress = (u) => {
    if (!u) return '';
    if (u.full_address) return u.full_address;
    const parts = [u.address, u.city, [u.state, u.zip_code].filter(Boolean).join(' ')].filter(Boolean);
    return parts.join(', ');
  };

  const [shippingAddress, setShippingAddress] = useState(editOrder?.shipping_address || getFullAddress(user) || user?.address || '');
  const [showAddressEdit, setShowAddressEdit] = useState(false);
  const [tempAddress, setTempAddress] = useState(getFullAddress(user) || user?.address || '');
  const [isSubscription, setIsSubscription] = useState(true);
  const [recurrence, setRecurrence] = useState('3');
  const [customerInfo, setCustomerInfo] = useState(null);
  const [orderBlocked, setOrderBlocked] = useState(false);

  // Payment state
  const [createdOrderId, setCreatedOrderId] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoResult, setPromoResult] = useState(null);
  const [promoError, setPromoError] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        const sizesData = await productsAPI.getSizes();
        // Transform sizes map into product-like objects for the dropdown
        const productsList = [];
        Object.entries(sizesData).forEach(([thickness, sizes]) => {
          sizes.forEach(size => productsList.push({ size, thickness }));
        });
        setProducts(productsList);
        if (user?.email) {
          try {
            const info = await api.get(`/orders/customer-info/${user.email}`);
            const ci = info.data;
            if (ci.found) {
              setCustomerInfo(ci);
              if (ci.delivery_address && ci.delivery_address.trim().toUpperCase() !== 'USA' && ci.delivery_address.trim().length > 5) setShippingAddress(ci.delivery_address);
              if (ci.order_blocked) setOrderBlocked(true);
            }
          } catch (e) {
            console.error('Customer info fetch failed:', e);
          }
        }
      } catch {
        toast({ title: 'Error', description: 'Failed to load products', variant: 'destructive' });
      } finally { setLoading(false); }
    };
    init();
  }, []);

  useEffect(() => {
    if (preselectedSize && !loading && products.length > 0) fetchPricing(0, preselectedSize, '8');
    // Fetch pricing for reorder items
    if (reorderItems && reorderItems.length > 0 && !loading && products.length > 0) {
      reorderItems.forEach((item, idx) => {
        if (item.size && item.merv) fetchPricing(idx, item.size, item.merv);
      });
    }
    if (editOrder?.items?.length > 0 && !loading && products.length > 0) {
      editOrder.items.forEach((item, idx) => {
        if (item.size && item.merv) fetchPricing(idx, item.size, String(item.merv));
      });
    }
  }, [preselectedSize, loading, products.length]);

  const fetchPricing = async (index, size, merv) => {
    if (!size || !merv) return;
    try {
      const pricing = await productsAPI.getPricing(size, parseInt(merv));
      const markupPct = customerInfo?.markup_percent || 0;
      const adjustedPrice = markupPct > 0 ? Math.round(pricing.price * (1 + markupPct / 100) * 100) / 100 : pricing.price;
      setOrderItems(prev => {
        const updated = [...prev];
        const moq = pricing.min_qty || 1;
        const currentQty = parseInt(updated[index].quantity) || 1;
        updated[index] = {
          ...updated[index], price: adjustedPrice, basePrice: pricing.price, minQty: moq,
          source: pricing.source || '', quantity: String(Math.max(currentQty, moq)),
        };
        return updated;
      });
    } catch (e) {
      console.error('Pricing fetch failed:', e);
      setOrderItems(prev => { const u = [...prev]; u[index] = { ...u[index], price: 0, basePrice: 0, source: '' }; return u; });
    }
  };

  const addItem = () => setOrderItems(prev => [...prev, { size: '', merv: '8', quantity: '1', price: 0, minQty: 1, source: '' }]);
  const removeItem = (index) => { if (orderItems.length > 1) setOrderItems(prev => prev.filter((_, i) => i !== index)); };

  const updateItem = (index, field, value) => {
    setOrderItems(prev => {
      const u = [...prev];
      let newValue = value;
      // Enforce min order quantity
      if (field === 'quantity') {
        const minQty = u[index].minQty || 1;
        const numVal = parseInt(value);
        if (!isNaN(numVal) && numVal < minQty) newValue = String(minQty);
      }
      u[index] = { ...u[index], [field]: newValue };
      return u;
    });
    const item = { ...orderItems[index], [field]: value };
    if ((field === 'size' || field === 'merv') && item.size && item.merv) fetchPricing(index, item.size, item.merv);
  };

  // Calculations
  const customerDiscountPct = customerInfo?.discount_percent || 0;
  const customerMarkupPct = customerInfo?.markup_percent || 0;
  const subDiscountPct = isSubscription ? SUBSCRIPTION_DISCOUNT : 0;
  const totalDiscountPct = customerDiscountPct + subDiscountPct;
  const subtotal = orderItems.reduce((sum, item) => sum + (item.price && item.quantity ? item.price * parseInt(item.quantity || 0) : 0), 0);
  const discount = totalDiscountPct > 0 ? Math.round(subtotal * (totalDiscountPct / 100) * 100) / 100 : 0;
  const promoDiscount = promoResult ? promoResult.discount_amount : 0;
  const totalDiscount = discount + promoDiscount;
  const afterDiscount = subtotal - totalDiscount;
  const shipping = afterDiscount >= 100 ? 0 : 15;
  const tax = Math.round(afterDiscount * TAX_RATE * 100) / 100;
  const total = Math.round((afterDiscount + shipping + tax) * 100) / 100;
  const totalFilters = orderItems.reduce((sum, item) => sum + parseInt(item.quantity || 0), 0);
  const deliveryDate = new Date(); deliveryDate.setDate(deliveryDate.getDate() + 10);

  const validateStep1 = () => {
    for (let i = 0; i < orderItems.length; i++) {
      const item = orderItems[i];
      if (!item.size || !item.merv || !item.quantity || parseInt(item.quantity) < 1) {
        toast({ title: 'Incomplete', description: `Complete all fields for row ${i + 1}`, variant: 'destructive' }); return false;
      }
      if (parseInt(item.quantity) < item.minQty) {
        toast({ title: 'Minimum Qty', description: `Row ${i + 1}: Min order is ${item.minQty} for ${item.size}`, variant: 'destructive' }); return false;
      }
    }
    if (subtotal === 0) { toast({ title: 'Empty Order', description: 'Please select at least one filter', variant: 'destructive' }); return false; }
    if (isSubscription && (!recurrence || recurrence === '0')) {
      toast({ title: 'Frequency Required', description: 'Please select a delivery frequency for your subscription', variant: 'destructive' }); return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!shippingAddress.trim()) { toast({ title: 'Address Required', description: 'Please provide a shipping address', variant: 'destructive' }); return false; }
    return true;
  };

  const handleNext = async () => {
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !validateStep2()) return;

    if (step === 2) {
      if (orderBlocked) { toast({ title: 'Orders Blocked', description: 'Your account is blocked from placing orders.', variant: 'destructive' }); return; }
      setPaymentLoading(true);
      try {
        let order;
        const orderPayload = {
          items: orderItems.map(item => ({ size: item.size, merv: parseInt(item.merv), quantity: parseInt(item.quantity), price: item.price })),
          shipping_address: shippingAddress, payment_method: 'QuickBooks Payments',
          is_subscription: isSubscription, recurrence_months: isSubscription ? parseInt(recurrence) : null,
          promo_code: promoResult ? promoResult.code : null,
        };
        if (editOrder?.id) {
          order = await ordersAPI.updateDraft(editOrder.id, orderPayload);
        } else {
          order = await ordersAPI.create({ customer_id: user.id, ...orderPayload });
        }
        setCreatedOrderId(order.id);
        setStep(3);
      } catch (error) {
        toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to prepare payment', variant: 'destructive' });
      } finally { setPaymentLoading(false); }
      return;
    }
    setStep(prev => Math.min(prev + 1, 3));
  };

  const handleBack = () => {
    setStep(prev => Math.max(prev - 1, 1));
  };

  const handlePaymentSuccess = (result) => {
    setPaymentSuccess(true);
    const orderId = result?.order_id || createdOrderId;
    toast({ title: 'Payment Successful!', description: 'Your order has been confirmed.' });
    setTimeout(() => navigate(`/customer/checkout-success?order_id=${orderId}`), 1500);
  };

  const handlePaymentError = (msg) => toast({ title: 'Payment Failed', description: msg, variant: 'destructive' });

  const handleSaveForLater = async () => {
    if (!validateStep1()) return;
    if (step === 2 && !validateStep2()) return;
    setPaymentLoading(true);
    try {
      const order = await ordersAPI.create({
        customer_id: user.id,
        items: orderItems.map(item => ({ size: item.size, merv: parseInt(item.merv), quantity: parseInt(item.quantity), price: item.price })),
        shipping_address: shippingAddress || 'Not provided', payment_method: 'QuickBooks Payments',
        is_subscription: isSubscription, recurrence_months: isSubscription ? parseInt(recurrence) : null,
        promo_code: promoResult ? promoResult.code : null,
      });
      toast({ title: 'Order Saved', description: `Draft order ${order.id} saved. You can pay later from My Orders.` });
      navigate('/customer/orders');
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to save order', variant: 'destructive' });
    } finally { setPaymentLoading(false); }
  };

  // Custom Size Dialog State
  const [showCustomSize, setShowCustomSize] = useState(false);
  const [customWidth, setCustomWidth] = useState('');
  const [customHeight, setCustomHeight] = useState('');
  const [customDepth, setCustomDepth] = useState('');
  const [customNotes, setCustomNotes] = useState('');
  const [customImages, setCustomImages] = useState([]);
  const [customSubmitting, setCustomSubmitting] = useState(false);
  const [customResult, setCustomResult] = useState(null);
  const fileInputRef = useRef(null);

  if (loading) return <Layout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sky-600" /></div></Layout>;

  const handleCustomImageAdd = (e) => {
    const files = Array.from(e.target.files || []);
    setCustomImages(prev => [...prev, ...files].slice(0, 5));
  };

  const handleCustomSubmit = async () => {
    if (!customWidth && !customHeight && !customDepth) {
      toast({ title: 'Missing Size', description: 'Please enter at least width and height', variant: 'destructive' });
      return;
    }
    setCustomSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('width', customWidth);
      formData.append('height', customHeight);
      formData.append('depth', customDepth || '1');
      formData.append('notes', customNotes);
      customImages.forEach(img => formData.append('images', img));

      const token = localStorage.getItem('at1s_token');
      const resp = await axios.post(`${API_BASE}/api/custom-size`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setCustomResult(resp.data);
      toast({ title: 'Request Submitted!', description: 'We\'ll send you a custom estimate soon.' });
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to submit', variant: 'destructive' });
    } finally { setCustomSubmitting(false); }
  };

  const resetCustomForm = () => {
    setCustomWidth(''); setCustomHeight(''); setCustomDepth(''); setCustomNotes('');
    setCustomImages([]); setCustomResult(null); setShowCustomSize(false);
  };

  const groupedProducts = {};
  products.forEach(p => { const t = p.thickness || '1'; if (!groupedProducts[t]) groupedProducts[t] = []; groupedProducts[t].push(p); });

  return (
    <Layout>
      <div className="max-w-5xl mx-auto" data-testid="new-order-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="order-page-title">
              {editOrder ? 'Edit Order' : step === 1 ? 'Select Your Filters' : step === 2 ? 'Shipping Details' : paymentSuccess ? 'Payment Complete!' : 'Review & Pay'}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {step === 1 ? 'Choose your filters and quantities' : step === 2 ? 'Confirm your delivery address' : paymentSuccess ? 'Redirecting to order confirmation...' : 'Review your order and enter payment details'}
            </p>
          </div>
          {step === 1 && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm"
                className="border-purple-200 text-purple-700 hover:bg-purple-50"
                onClick={() => setShowCustomSize(true)}
                data-testid="custom-size-btn"
              >
                <Ruler className="h-4 w-4 mr-1" /> Custom Size
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigate('/customer/dashboard')} data-testid="back-btn">
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
            </div>
          )}
        </div>

        {/* Step Indicator */}
        <div className="flex items-center mb-8" data-testid="step-indicator">
          {STEPS.map((s, idx) => {
            const Icon = s.icon;
            const isActive = step === s.id;
            const isCompleted = step > s.id || (s.id === 3 && paymentSuccess);
            return (
              <React.Fragment key={s.id}>
                <div className="flex items-center gap-2.5">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                    isActive ? 'bg-sky-600 text-white shadow-lg shadow-sky-600/30' :
                    isCompleted ? 'bg-teal-500 text-white' : 'bg-slate-200 text-slate-500'
                  }`} data-testid={`step-circle-${s.id}`}>
                    {isCompleted ? <CheckCircle className="h-4.5 w-4.5" /> : <Icon className="h-4 w-4" />}
                  </div>
                  <span className={`text-sm font-medium hidden sm:block ${isActive ? 'text-sky-700' : isCompleted ? 'text-teal-600' : 'text-slate-400'}`}>
                    {s.label}
                  </span>
                </div>
                {idx < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-3 rounded transition-colors ${step > s.id ? 'bg-teal-400' : 'bg-slate-200'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {orderBlocked && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-5 flex items-center gap-2" data-testid="blocked-banner">
            <Ban className="h-5 w-5 text-red-600 flex-shrink-0" />
            <p className="text-sm font-medium text-red-800">Your account is currently blocked from placing orders. Please contact support.</p>
          </div>
        )}

        {step === 1 && (
          <FilterSelector
            orderItems={orderItems} groupedProducts={groupedProducts} totalFilters={totalFilters}
            customerDiscountPct={customerDiscountPct} customerMarkupPct={customerMarkupPct}
            isSubscription={isSubscription} setIsSubscription={setIsSubscription}
            recurrence={recurrence} setRecurrence={setRecurrence} updateItem={updateItem} addItem={addItem} removeItem={removeItem}
          />
        )}

        {step === 2 && (
          <ShippingStep
            user={user} shippingAddress={shippingAddress} orderItems={orderItems} subtotal={subtotal} deliveryDate={deliveryDate}
            showAddressEdit={showAddressEdit} setShowAddressEdit={setShowAddressEdit} tempAddress={tempAddress} setTempAddress={setTempAddress}
            setShippingAddress={setShippingAddress} toast={toast}
            promoCode={promoCode} setPromoCode={setPromoCode}
            promoResult={promoResult} setPromoResult={setPromoResult}
            promoError={promoError} setPromoError={setPromoError}
            promoLoading={promoLoading} setPromoLoading={setPromoLoading}
          />
        )}

        {step === 3 && (
          <ReviewAndPay
            orderItems={orderItems} user={user} shippingAddress={shippingAddress} isSubscription={isSubscription}
            recurrence={recurrence} subtotal={subtotal} customerDiscountPct={customerDiscountPct} discount={totalDiscount}
            afterDiscount={afterDiscount} shipping={shipping} tax={tax} total={total} totalFilters={totalFilters}
            deliveryDate={deliveryDate} totalDiscountPct={totalDiscountPct}
            createdOrderId={createdOrderId} paymentSuccess={paymentSuccess}
            onPaymentSuccess={handlePaymentSuccess} onPaymentError={handlePaymentError}
            onBack={() => setStep(2)} onAddressChange={(addr) => setShippingAddress(addr)}
            promoResult={promoResult} promoDiscount={promoDiscount}
          />
        )}

        {step < 3 && (
          <div className="flex items-center justify-between mt-6 pt-4 border-t">
            {step > 1 ? (
              <Button variant="outline" onClick={handleBack} data-testid="step-back-btn"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
            ) : <div />}
            <div className="flex items-center gap-3">
              {step === 2 && (
                <Button variant="outline" onClick={handleSaveForLater} disabled={paymentLoading}
                  className="text-slate-600" data-testid="save-for-later-btn">
                  Save for Later
                </Button>
              )}
              <Button onClick={handleNext} disabled={paymentLoading}
                className="bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white font-bold px-8"
                data-testid="step-next-btn">
                {paymentLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Preparing Payment...</> : <>Continue <ArrowRight className="h-4 w-4 ml-1" /></>}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Custom Size Request Dialog */}
      <Dialog open={showCustomSize} onOpenChange={v => { if (!v) resetCustomForm(); }}>
        <DialogContent className="max-w-lg" data-testid="custom-size-dialog">
          {customResult ? (
            <div className="text-center py-4 space-y-4">
              <CheckCircle className="h-14 w-14 text-green-500 mx-auto" />
              <div>
                <h3 className="text-lg font-bold text-slate-800">Request Submitted!</h3>
                <p className="text-sm text-slate-500 mt-1">ID: <span className="font-mono text-purple-600">{customResult.id}</span></p>
                <p className="text-sm text-slate-600 mt-2">We'll review your custom size and send you an estimate via email.</p>
              </div>
              <div className="flex flex-col gap-2">
                <a
                  href={`sms:?body=${encodeURIComponent(`Custom Size Request ${customResult.id}`)}`}
                  className="w-full inline-flex items-center justify-center rounded-md text-sm font-medium h-10 px-4 py-2 bg-sky-600 text-white hover:bg-sky-700"
                  data-testid="custom-size-sms-btn"
                >
                  <Send className="h-4 w-4 mr-2" /> Text Us for Faster Response
                </a>
                <Button variant="outline" onClick={resetCustomForm} data-testid="custom-size-close-btn">Close</Button>
              </div>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><Ruler className="h-5 w-5 text-purple-600" /> Request Custom Size Filter</DialogTitle>
                <DialogDescription>Enter your custom dimensions and upload a photo. We'll send you a separate estimate.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">Width (inches)</Label>
                    <Input value={customWidth} onChange={e => setCustomWidth(e.target.value)} placeholder="20" data-testid="custom-width" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Height (inches)</Label>
                    <Input value={customHeight} onChange={e => setCustomHeight(e.target.value)} placeholder="25" data-testid="custom-height" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Depth (inches)</Label>
                    <Input value={customDepth} onChange={e => setCustomDepth(e.target.value)} placeholder="1" data-testid="custom-depth" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Additional Notes</Label>
                  <textarea
                    value={customNotes}
                    onChange={e => setCustomNotes(e.target.value)}
                    className="w-full border rounded-lg p-2.5 text-sm text-slate-700 focus:ring-2 focus:ring-purple-300 focus:border-purple-400 outline-none resize-none"
                    rows={2}
                    placeholder="Describe the filter type, MERV rating needed, or any special requirements..."
                    data-testid="custom-notes"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Upload Photos (optional, max 5)</Label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {customImages.map((img, i) => (
                      <div key={img.name + i} className="relative w-16 h-16 rounded-lg border overflow-hidden">
                        <img src={URL.createObjectURL(img)} alt="" className="w-full h-full object-cover" />
                        <button onClick={() => setCustomImages(prev => prev.filter((_, j) => j !== i))} className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                    {customImages.length < 5 && (
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="w-16 h-16 rounded-lg border-2 border-dashed border-purple-300 flex flex-col items-center justify-center text-purple-400 hover:border-purple-500 hover:text-purple-600 transition-colors"
                        data-testid="custom-upload-btn"
                      >
                        <Camera className="h-5 w-5" />
                        <span className="text-[10px]">Add</span>
                      </button>
                    )}
                  </div>
                  <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleCustomImageAdd} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={resetCustomForm}>Cancel</Button>
                <Button
                  onClick={handleCustomSubmit}
                  disabled={customSubmitting}
                  className="bg-purple-600 hover:bg-purple-700"
                  data-testid="custom-size-submit-btn"
                >
                  {customSubmitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting...</> : <><Send className="h-4 w-4 mr-2" /> Submit Request</>}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default NewOrder;
